import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  ArrowLeft,
  Upload,
  X,
  FileText,
  Image,
  Send,
  Bell,
  BellOff,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Wrench,
  HelpCircle,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

type TicketStatus = "OPEN" | "IN_PROGRESS" | "RESOLVED" | "BLOCKED";
type TicketCategory = "GENERAL" | "URGENT" | "MAINTENANCE";

const statusLabels: Record<TicketStatus, string> = {
  OPEN: "Abierto",
  IN_PROGRESS: "En progreso",
  RESOLVED: "Resuelto",
  BLOCKED: "Bloqueado",
};

const statusColors: Record<
  TicketStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  OPEN: "default",
  IN_PROGRESS: "secondary",
  RESOLVED: "outline",
  BLOCKED: "destructive",
};

const statusIcons: Record<
  TicketStatus,
  React.ComponentType<{ className?: string }>
> = {
  OPEN: Clock,
  IN_PROGRESS: Loader2,
  RESOLVED: CheckCircle,
  BLOCKED: XCircle,
};

const categoryLabels: Record<TicketCategory, string> = {
  GENERAL: "General",
  URGENT: "Urgente",
  MAINTENANCE: "Mantenimiento",
};

const categoryIcons: Record<
  TicketCategory,
  React.ComponentType<{ className?: string }>
> = {
  GENERAL: HelpCircle,
  URGENT: AlertTriangle,
  MAINTENANCE: Wrench,
};

interface UploadedFile {
  name: string;
  url: string;
  type: string;
}

function formatDateTime(date: Date | string) {
  return new Date(date).toLocaleDateString("es-AR", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getInitials(name: string | null | undefined) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function TicketDetailPage() {
  const navigate = useNavigate();
  const { ticketId } = useParams({ from: "/tickets/$ticketId" });
  const utils = trpc.useUtils();

  const [newComment, setNewComment] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);

  const { data: ticket, isLoading } = trpc.ticket.getById.useQuery(
    { ticketId },
    { enabled: !!ticketId },
  );

  const updateStatus = trpc.ticket.updateStatus.useMutation({
    onSuccess: () => {
      utils.ticket.getById.invalidate({ ticketId });
      utils.ticket.list.invalidate();
      toast.success("Estado actualizado");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const addComment = trpc.ticket.addComment.useMutation({
    onSuccess: () => {
      utils.ticket.getById.invalidate({ ticketId });
      utils.ticket.list.invalidate();
      setNewComment("");
      setUploadedFiles([]);
      toast.success("Comentario agregado");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const subscribe = trpc.ticket.subscribe.useMutation({
    onSuccess: () => {
      utils.ticket.getById.invalidate({ ticketId });
      toast.success("Suscripto a notificaciones del ticket");
    },
  });

  const unsubscribe = trpc.ticket.unsubscribe.useMutation({
    onSuccess: () => {
      utils.ticket.getById.invalidate({ ticketId });
      toast.success("Desuscripto de notificaciones del ticket");
    },
  });

  const getUploadUrl = trpc.upload.getUploadUrl.useMutation();

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);

    try {
      for (const file of Array.from(files)) {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(
            `El archivo ${file.name} es demasiado grande. Máximo 10MB.`,
          );
          continue;
        }

        const { uploadUrl, downloadUrl, fileName } =
          await getUploadUrl.mutateAsync({
            fileName: file.name,
            contentType: file.type,
            fileSize: file.size,
            context: "ticket-comment",
            contextId: ticketId,
          });

        const response = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });

        if (!response.ok) {
          throw new Error(`No se pudo subir ${file.name}`);
        }

        setUploadedFiles((prev) => [
          ...prev,
          { name: fileName, url: downloadUrl, type: file.type },
        ]);
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "No se pudo subir el archivo",
      );
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitComment = () => {
    if (!newComment.trim()) return;

    addComment.mutate({
      ticketId,
      content: newComment,
      mediaUrls: uploadedFiles.map((f) => f.url),
    });
  };

  const isSubscribed = ticket?.subscribers.some(
    (s) => s.memberId === ticket.currentMemberId,
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate({ to: "/tickets" })}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver a Tickets
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Ticket no encontrado.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const CategoryIcon = categoryIcons[ticket.category];
  const StatusIcon = statusIcons[ticket.status];

  return (
    <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
      <Button
        variant="ghost"
        className="mb-6"
        onClick={() => navigate({ to: "/tickets" })}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Volver a Tickets
      </Button>

      {/* Ticket Header */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
                <CategoryIcon className="h-6 w-6" />
              </div>
              <div>
                <CardTitle className="text-xl">{ticket.title}</CardTitle>
                <CardDescription className="mt-1 flex flex-wrap items-center gap-2">
                  <Badge
                    variant={statusColors[ticket.status]}
                    className="text-xs"
                  >
                    <StatusIcon className="mr-1 h-3 w-3" />
                    {statusLabels[ticket.status]}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {categoryLabels[ticket.category]}
                  </Badge>
                  <span className="text-xs">
                    Creado {formatDateTime(ticket.createdAt)}
                  </span>
                </CardDescription>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Subscribe/Unsubscribe button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  isSubscribed
                    ? unsubscribe.mutate({ ticketId })
                    : subscribe.mutate({ ticketId })
                }
                disabled={subscribe.isPending || unsubscribe.isPending}
              >
                {isSubscribed ? (
                  <>
                    <BellOff className="mr-2 h-4 w-4" />
                    Dejar de seguir
                  </>
                ) : (
                  <>
                    <Bell className="mr-2 h-4 w-4" />
                    Seguir
                  </>
                )}
              </Button>

              {/* Admin: Status dropdown */}
              {ticket.isAdmin && (
                <Select
                  value={ticket.status}
                  onValueChange={(value: TicketStatus) =>
                    updateStatus.mutate({ ticketId, status: value })
                  }
                  disabled={updateStatus.isPending}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OPEN">Abierto</SelectItem>
                    <SelectItem value="IN_PROGRESS">En progreso</SelectItem>
                    <SelectItem value="RESOLVED">Resuelto</SelectItem>
                    <SelectItem value="BLOCKED">Bloqueado</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Description */}
          <div className="whitespace-pre-wrap text-sm">
            {ticket.description}
          </div>

          {/* Attachments */}
          {ticket.mediaUrls.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-sm font-medium">Adjuntos</p>
              <div className="flex flex-wrap gap-2">
                {ticket.mediaUrls.map((url, index) => (
                  <a
                    key={index}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-muted"
                  >
                    <FileText className="h-4 w-4" />
                    Adjunto {index + 1}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Requester info */}
          <div className="mt-4 flex items-center gap-3 border-t pt-4">
            <Avatar className="h-8 w-8">
              <AvatarImage src={ticket.requester.image || undefined} />
              <AvatarFallback>
                {getInitials(ticket.requester.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-sm font-medium">
                {ticket.requester.name || "Sin nombre"}
              </p>
              <p className="text-xs text-muted-foreground">Solicitante</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Comments Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Comentarios</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Existing comments */}
          {ticket.comments.length > 0 ? (
            <div className="space-y-4">
              {ticket.comments.map((comment) => (
                <div key={comment.id} className="flex gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={comment.author.image || undefined} />
                    <AvatarFallback>
                      {getInitials(comment.author.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {comment.author.name || "Sin nombre"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(comment.createdAt)}
                      </span>
                    </div>
                    <div className="mt-1 whitespace-pre-wrap text-sm">
                      {comment.content}
                    </div>
                    {comment.mediaUrls.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {comment.mediaUrls.map((url, index) => (
                          <a
                            key={index}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-muted"
                          >
                            <FileText className="h-3 w-3" />
                            Archivo {index + 1}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-sm text-muted-foreground">
              No hay comentarios todavía. Sé el primero en comentar.
            </p>
          )}

          {/* Add comment form */}
          <div className="border-t pt-4">
            <Textarea
              placeholder="Escribí un comentario..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              rows={3}
            />

            {/* File upload for comment */}
            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  className="hidden"
                  id="comment-file-upload"
                  accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                  multiple
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    document.getElementById("comment-file-upload")?.click()
                  }
                  disabled={uploading}
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                </Button>

                {uploadedFiles.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {uploadedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-1 rounded bg-muted px-2 py-1 text-xs"
                      >
                        {file.type.startsWith("image/") ? (
                          <Image className="h-3 w-3" />
                        ) : (
                          <FileText className="h-3 w-3" />
                        )}
                        <span className="max-w-[100px] truncate">
                          {file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                onClick={handleSubmitComment}
                disabled={!newComment.trim() || addComment.isPending}
              >
                {addComment.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Enviar
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
